import { getProfessionCategoryImage } from './images.js'
import { professionImageOverrides } from './extraImages.js'
import { additionalProfessionIdsNeedingSpecificVisual } from './professionImageFit.js'
import { generatedProfessionImages, generatedProfessionImagePaths } from './generatedImages.js'
import { generatedProfessionImageAlts } from './generatedImageAlts.js'
import { professionNameInSentence } from './professionCopy.js'

const hubImageKinds = ['hero', 'detail', 'visit']
const childImageKinds = [
  'admin-hero', 'admin-support',
  'leads-hero', 'leads-support',
  'booking-hero', 'booking-support',
  'reminders-hero', 'reminders-support',
  'records-hero', 'records-support',
]

function generatedAlt(profession, kind) {
  const role = professionNameInSentence(profession.name)
  if (kind === 'detail' && profession.slug === 'locksmith') return 'Uninstalled door handles prepared for a locksmith appointment'
  if (kind === 'detail' && profession.slug === 'safe-engineer') return 'Closed office safe beside a blank appointment diary and work bag'
  if (kind === 'visit' && profession.slug === 'locksmith') return 'Locksmith and homeowner discussing a future appointment outside a closed front door'
  if (kind === 'visit' && profession.slug === 'safe-engineer') return 'Safe engineer checking in at an office reception with a closed safe behind'
  if (kind === 'visit' && profession.slug === 'family-therapist') return 'Family therapist facilitating a discussion with two adult family members'
  if (kind === 'visit' && profession.slug === 'play-therapist') return 'Play therapist guiding a child using a sand tray with a guardian nearby'
  const descriptions = {
    hero: `${profession.name} at work`,
    detail: `Tools and materials for ${role} work`,
    visit: `${profession.name} with another person during a work assignment`,
    'admin-hero': `${profession.name} reviewing the working day between assignments`,
    'admin-support': `${profession.name} finishing one assignment and preparing another`,
    'leads-hero': `${profession.name} discussing possible work with a prospective client`,
    'leads-support': `${profession.name} assessing the requested work with a prospective client`,
    'booking-hero': `${profession.name} focused on the work at hand`,
    'booking-support': `${profession.name} agreeing a time for work with a client`,
    'reminders-hero': `${profession.name} checking details before a scheduled job`,
    'reminders-support': `${profession.name} arriving for scheduled work`,
    'records-hero': `${profession.name} reviewing previous business booking details`,
    'records-support': `${profession.name} recording completed work and the next step`,
  }
  return descriptions[kind] || `${profession.name} at work`
}

function generatedProfessionImage(profession) {
  const kinds = generatedProfessionImages[profession.slug]
  if (!kinds?.includes('hero')) return null
  const visual = (kind) => ({
    src: generatedProfessionImagePaths[profession.slug][kind],
    alt: generatedProfessionImageAlts[profession.slug]?.[kind] || generatedAlt(profession, kind),
  })
  const hero = visual('hero')
  return {
    ...hero,
    kind: 'generated-editorial',
    gallery: hubImageKinds.every((kind) => kinds.includes(kind)) ? hubImageKinds.map(visual) : undefined,
    scenes: Object.fromEntries(childImageKinds.filter((kind) => kinds.includes(kind)).map((kind) => [kind, visual(kind)])),
  }
}

// A category photograph is only useful when it actually illustrates the role.
// The calendar-only phone visual is a truthful product fallback for the broad
// categories where the representative photo would depict a different trade.
export const productImageFallback = Object.freeze({
  src: '/public-assets/homepage-platform/offlico-professional-app-iphone-calendar.webp',
  alt: 'Offlico weekly appointment calendar shown on a phone',
  width: 600,
  height: 1086,
  kind: 'product',
})

function idRange(first, last) {
  return Array.from({ length: last - first + 1 }, (_, index) => `MP${String(first + index).padStart(3, '0')}`)
}

export const professionIdsNeedingSpecificVisual = new Set([
  // Medical props are not a truthful illustration of optical, dental,
  // maternity, hearing, imaging or prosthetic work.
  ...idRange(1, 7), ...idRange(10, 14),  'MP016', 'MP017', 'MP019', 'MP020',
  // Hair cutting does not illustrate skin, nail, tattoo or body-art services.
  'MP049', 'MP051', 'MP052', 'MP053', ...idRange(56, 64),
  // Dog grooming and horse shoeing are not the rest of animal services.
  ...idRange(123, 130), 'MP132', 'MP133', 'MP134',
  'MP136', 'MP137', 'MP138', 'MP139', ...idRange(141, 147),
  // A refuse collection photo is not pest, asbestos or water-hygiene work.
  'MP211', ...idRange(213, 217), 'MP219',
  // A car-bonnet repair does not depict cycle, marine or many vehicle trades.
  'MP254', ...idRange(256, 261), ...idRange(263, 271),
  // Delivery boxes do not depict passenger or heavy-load transport.
  ...idRange(317, 321), 'MP323', ...idRange(325, 327), 'MP330', 'MP331',
  // A photographer is not an art restorer, muralist or sound recordist.
  ...idRange(340, 344), 'MP346', 'MP347', ...idRange(349, 352),
  // A performance stage does not depict planning, decor or venue services.
  ...idRange(356, 360), 'MP365', ...idRange(367, 370), 'MP373', 'MP377', 'MP381',
  // A kitchen chef is not a bartender, barista or front-of-house worker.
  ...idRange(383, 386), 'MP388',
  // Further individual category-photo exceptions from a 429-role fit review.
  ...additionalProfessionIdsNeedingSpecificVisual,
])

export function getProfessionHeroImage(profession) {
  const generatedImage = generatedProfessionImage(profession)
  if (generatedImage) return generatedImage
  const specificImage = professionImageOverrides[profession.id]
  if (specificImage) return specificImage
  if (professionIdsNeedingSpecificVisual.has(profession.id)) return productImageFallback
  return getProfessionCategoryImage(profession.categorySlug) || productImageFallback
}
